import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Password} from "primereact/password";
import {Button} from "primereact/button";
import {LoginDialogDefinition, LoginDialogProperties} from "../../../shared/dialog/components/LoginDialog";
import LoginDialogFields from "../../../shared/dialog/fields/LoginDialogFields";
import {LoginType} from "../../../shared/models/LoginType";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import portalConfiguration from "../../../shared/configuration/PortalConfiguration";
import Portal from "../../../portal/Portal";
import {AuthProvider, Information, LocalAuth} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import OAuth from "../../../oauth/OAuth";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import PKCE from "js-pkce";
import StorageItem from "../../../shared/StorageItem";
import Admin from "../../Admin";
import MessageBox from "../../../shared/components/MessageBox";

export class AdminLoginDialogDefinition extends LoginDialogDefinition {
	protected renderButtons(): ReactNode {
		let portalInfo: Information = this.props.context.info;
		let providerButtons: Array<ReactNode> = [];
		let portalUrl = portalConfiguration.portalUrl.toString();

		for (let oauthProvider of FunctionHelper.applyValue(portalInfo.oauth, Information.getOauthDefault())) {
			let config: any = {
				client_id: oauthProvider.clientId,
				redirect_uri: portalUrl + OAuth.route,
				authorization_endpoint: oauthProvider.oidc?.authorizationEndpoint,
				token_endpoint: oauthProvider.oidc?.tokenEndpoint
			};

			if (FunctionHelper.exists(oauthProvider.scope) && oauthProvider.scope !== "") {
				config.requested_scopes = oauthProvider.scope;
			}

			let pkce: PKCE = new PKCE(config);

			let additionalParams: any = {
				state: "webPDF-Portal"
			};

			if (FunctionHelper.exists(oauthProvider.applicationId) && oauthProvider.applicationId !== "") {
				additionalParams.audience = oauthProvider.applicationId;
			}

			providerButtons.push(
				<Button key={oauthProvider.name}
						onClick={async () => {
							sessionStorage.setItem(StorageItem.OAUTH_REDIRECT, portalUrl + Admin.route);
							localStorage.setItem(StorageItem.PROVIDER, oauthProvider.name!);
							window.location.href = pkce.authorizeUrl(additionalParams);
						}}>
					{this.renderIcon(oauthProvider)}
					<Translate {...this.props} data={"login.provider"} namespace="dialog"
							   values={{
								   provider: FunctionHelper.applyStringValue(
									   oauthProvider.displayName, oauthProvider.name, AuthProvider.getNameDefault()
								   )
							   }}/>
				</Button>
			)
		}

		if (providerButtons.length === 0) {
			return null;
		}

		return (
			<div className="provider-buttons p-col-12 p-grid">
				<div className="p-col-12">
					<h2><span><Translate {...this.props} data="login.or" namespace="dialog"/></span></h2>
				</div>
				<div className="p-col-12 p-grid">
					{providerButtons}
				</div>
			</div>
		);
	}

	public renderDialog(): ReactNode {
		let parameter: LoginType | undefined = this.state.parameter;
		let errors: Map<LoginDialogFields, string> = this.state.fieldErrors;
		let portalInfo: Information = this.props.context.info;

		let classList: Array<string> = ["login-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					closeOnEscape={false} header={this.renderHeader()} footer={<div/>}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{!FunctionHelper.applyValue(portalInfo.localAuth?.apiEnabled, LocalAuth.getApiEnabledDefault()) ?
						<MessageBox {...this.props} type={MessageType.WARNING} className="p-col-12">
							<Translate {...this.props} data="infos.portal.disabled" namespace="message"/>
						</MessageBox> :
						<>
							{this.renderError()}
							<Helper {...this.props} type={MessageType.SUCCESS}>
								<Translate {...this.props} data={"dialog.admin.login.hint"} namespace={"help"}
										   markdown={true}/>
							</Helper>
							<fieldset className="p-col-12 p-grid">
								<div className="p-col-12 p-grid"
									 hidden={!this.state.fields?.contains(LoginDialogFields.USERNAME)}>
									<label className="p-col-12 p-sm-4" htmlFor="control-username">
										<Translate {...this.props} data="login.username" namespace="dialog"/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<InputText id="control-username" autoFocus={true}
												   className={errors.has(LoginDialogFields.USERNAME) ? "p-invalid" : ""}
												   tooltip={errors.has(LoginDialogFields.USERNAME) ? Translate.value(
													   errors.get(LoginDialogFields.USERNAME)!, {ns: "message"}
												   ) : ""}
												   value={FunctionHelper.applyValue(
													   parameter?.user, LoginType.getUserDefault()
												   )}
												   onInput={(e) => this.onChange(
													   LoginDialogFields.USERNAME, e.currentTarget.value
												   )}/>
										{this.renderFieldError(errors.has(LoginDialogFields.USERNAME) ?
											Translate.value(
												errors.get(LoginDialogFields.USERNAME)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
								<div className="p-col-12 p-grid"
									 hidden={!this.state.fields?.contains(LoginDialogFields.PASSWORD)}>
									<label className="p-col-12 p-sm-4" htmlFor="control-password">
										<Translate {...this.props} data="login.password" namespace="dialog"/>
									</label>
									<div className="p-col-12 p-sm-8 p-grid">
										<Password id="control-password" feedback={false} toggleMask={true}
												  className={errors.has(LoginDialogFields.PASSWORD) ? "p-invalid" : ""}
												  tooltip={errors.has(LoginDialogFields.PASSWORD) ? Translate.value(
													  errors.get(LoginDialogFields.PASSWORD)!, {ns: "message"}
												  ) : ""}
												  value={FunctionHelper.applyValue(
													  parameter?.password, LoginType.getPasswordDefault()
												  )}
												  onInput={(e) => this.onChange(
													  LoginDialogFields.PASSWORD, e.currentTarget.value
												  )}/>
										{this.renderFieldError(errors.has(LoginDialogFields.PASSWORD) ?
											Translate.value(
												errors.get(LoginDialogFields.PASSWORD)!, {ns: "message"}
											) : ""
										)}
									</div>
								</div>
								<div className="p-col-12 p-grid">
									{this.renderFooter()}
								</div>
								{this.renderButtons()}
							</fieldset>
						</>
					}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					{this.renderHeaderIcon()}
					<Translate {...this.props} data="login.title" namespace="dialog"/>
				</h1>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid" style={{
				justifyContent: "flex-end"
			}}>
				<Button label={Translate.value("buttons.login")} className="primary-button"
						icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-login")}
						disabled={!this.isValid()} onClick={() => this.submit()}/>
				<Button label={Translate.value("buttons.back_to_portal")} icon="icon icon-home"
						className="dialog-button" style={{marginLeft: "20px"}}
						onClick={() => {
							window.location.href = portalConfiguration.portalUrl.toString() + Portal.route;
						}}/>
			</div>
		);
	}

	protected async submit(): Promise<void> {
		this.setState({
			info: undefined,
			exception: undefined,
			submitted: true
		});

		let operationData: LoginType = this.state.parameter!.clone();

		try {
			await this.props.context.session.login(undefined, operationData.user, operationData.password);
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		this.props.context.getPage().addMessage(
			new Message("success.authentication.login", MessageType.SUCCESS)
		);
		this.hide();

		if (window.location.search !== "") {
			window.location.reload();
		}
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (!this.mounted || !this.props.visible) {
			return;
		}

		if (event.key.toLowerCase() === "enter") {
			if (this.isValid() && !this.state.submitted) {
				await this.submit();
			}
		}
	}
}

const AdminLoginDialog = React.memo(AdminLoginDialogDefinition,
	(prevProps: Readonly<LoginDialogProperties>, nextProps: Readonly<LoginDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default AdminLoginDialog;